import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemText,
  TableSortLabel
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import ColumnFilter, { FilterConfig } from './ColumnFilter';

interface Shop {
  id: number;
  name: string;
  address: string;
}

const Shops: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [showAddShopDialog, setShowAddShopDialog] = useState(false);
  const [newShop, setNewShop] = useState({ name: '', address: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Shop>('name');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'name', type: 'string', label: 'Shop Name', placeholder: 'Filter by shop name' },
    { field: 'address', type: 'string', label: 'Address', placeholder: 'Filter by address' }
  ];

  useEffect(() => {
    if (token) {
      fetchShops();
    }
  }, [token]);

  const fetchShops = async () => {
    setIsLoadingShops(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/shops`, {
        headers: { Authorization: token }
      });
      setShops(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching shops:', err);
      }
    } finally {
      setIsLoadingShops(false);
    }
  };

  const handleAddShop = async () => {
    if (!newShop.name.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API_BASE_URL}/shops`, newShop, {
        headers: { Authorization: token }
      });
      
      setShowAddShopDialog(false);
      setNewShop({ name: '', address: '' });
      fetchShops();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to add shops');
      } else {
        console.error('Failed to add shop');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add':
        setShowAddShopDialog(true);
        break;
    }
  };

  const handleSort = (property: keyof Shop) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const sortedShops = [...shops].filter(shop => {
    return Object.entries(columnFilters).every(([field, filterValue]) => {
      if (!filterValue || filterValue === '') return true;
      
      switch (field) {
        case 'name':
          return shop.name.toLowerCase().includes(filterValue.toLowerCase());
        case 'address':
          return shop.address.toLowerCase().includes(filterValue.toLowerCase());
        default:
          return true;
      }
    });
  }).sort((a, b) => {
    let aValue: any = a[orderBy];
    let bValue: any = b[orderBy];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {hasPermission('add-shop') && (
            <>
              <Button
                variant="outlined"
                onClick={handleActionsMenuOpen}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    width: '100%',
                    maxWidth: '280px'
                  }
                }}
              >
                <MenuItem onClick={() => handleActionSelect('add')}>
                  <ListItemText primary="Add Shop" />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, maxWidth: '600px' }}>
              {/* Search can be added here if needed */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              {hasPermission('add-shop') && (
                <Button 
                  variant="contained" 
                  onClick={() => setShowAddShopDialog(true)}
                  size="small"
                >
                  Add Shop
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          '& .MuiTableCell-root': {
            padding: isMobile ? '6px 8px' : '8px 16px',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Shop Name
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.name}
                    onChange={(value) => handleColumnFilterChange('name', value)}
                    onClear={() => handleColumnFilterClear('name')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '200px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'address'}
                    direction={orderBy === 'address' ? order : 'asc'}
                    onClick={() => handleSort('address')}
                  >
                    Address
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.address}
                    onChange={(value) => handleColumnFilterChange('address', value)}
                    onClear={() => handleColumnFilterClear('address')}
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingShops ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              sortedShops.map((shop) => (
                <TableRow key={shop.id}>
                  <TableCell>{shop.name}</TableCell>
                  <TableCell>{shop.address}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Shop Dialog */}
      <Dialog open={showAddShopDialog} onClose={() => setShowAddShopDialog(false)}>
        <DialogTitle>Add New Shop</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Shop Name"
            fullWidth
            value={newShop.name}
            onChange={(e) => setNewShop({ ...newShop, name: e.target.value })}
            disabled={isSubmitting}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            multiline
            rows={2}
            value={newShop.address}
            onChange={(e) => setNewShop({ ...newShop, address: e.target.value })}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddShopDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleAddShop} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Add Shop'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shops;
